// src/views/whatsappInbox/components/MessageMedia/index.tsx

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { IoIosCloudDownload } from "react-icons/io";
import { Spin, Modal } from 'antd';
import { FileText, Video, Image as ImageIcon, X, PlayIcon } from 'lucide-react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { 
  downloadMedia, 
  openMediaPreview,
  selectIsMediaDownloading,
  selectMediaError
} from '../../../store/slices/mediaSlice';
import logger, { Logger } from '../../../utils/logger';
import toast from 'react-hot-toast';

// Styled Components
const MediaWrapper = styled.div<{ thumbnailMode?: boolean }>`
  max-width: 350px; // Increased from default
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
  margin: 4px 0;
`;


const EnhancedAudioPlayer = styled.div`
  width: 100%;
  background-color: #f0f2f5;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  .message.outgoing & {
    background-color: #dcf8c6;
  }
`;

const WaveformVisualization = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  gap: 2px;
  margin-bottom: 4px;
`;

const WaveformBar = styled.div<{ isPlayed: boolean }>`
  flex: 1;
  border-radius: 1px;
  background-color: ${props => props.isPlayed ? '#0084ff' : '#e1e1e1'};
  min-width: 2px;

  .message.outgoing & {
    background-color: ${props => props.isPlayed ? '#25d366' : '#e1e1e1'};
  }
`;

const AudioProgress = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  margin-bottom: 4px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  background: #0084ff;
  position: absolute;
  left: 0;
  top: 0;
  width: ${props => props.progress}%;
  transition: width 0.1s;

  .message.outgoing & {
    background: #25d366;
  }
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #8696a0;
  margin-bottom: 8px;
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: none;
  color: #54656f;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f5f5f5;
  }
`;

const PlayButton = styled(ControlButton)`
  background: #0084ff;
  color: white;
  
  .message.outgoing & {
    background: #25d366;
  }
  
  &:hover {
    background: #0077e6;
    
    .message.outgoing & {
      background: #22c35e;
    }
  }
`;

const SpeedIndicator = styled.span`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
`;

const AudioWrapper = styled.div`
  padding: 8px 12px;
  background: #f0f2f5;
  border-radius: 8px;
  width: 350px; // Increased from 280px to match the screenshot
  max-width: 350px;

  audio {
    width: 100%;
    height: 40px;
  }

  audio::-webkit-media-controls-panel {
    background-color: #ffffff;
  }

  audio::-webkit-media-controls-play-button {
    background-color: #dcf8c6;
    border-radius: 50%;
  }

  audio::-webkit-media-controls-timeline {
    background-color: #f0f2f5;
    border-radius: 2px;
    height: 3px;
  }
`;

const MediaPlaceholder = styled.div<{ thumbnailMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  padding: ${props => props.thumbnailMode ? '12px' : '24px'};
  gap: ${props => props.thumbnailMode ? '8px' : '12px'};
  min-height: ${props => props.thumbnailMode ? '100%' : '120px'};
  cursor: pointer;
  
  &:hover {
    background: #e5e7eb;
  }

  .icon-wrapper {
    background: white;
    padding: ${props => props.thumbnailMode ? '8px' : '12px'};
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .text {
    font-size: 13px;
    color: #64748b;
    text-align: center;
  }
`;

const MediaContent = styled.div`
  position: relative;
  cursor: pointer;
`;

const ImagePreview = styled.img<{ thumbnailMode?: boolean }>`
  width: 100%;
  height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  display: block;
  max-height: ${props => props.thumbnailMode ? 'none' : '300px'};
  object-fit: ${props => props.thumbnailMode ? 'cover' : 'contain'};
  border-radius: 8px;
`;

// VIDEO COMPONENT IMPROVEMENTS
// Update the VideoPreview component
const VideoPreview = styled.video<{ thumbnailMode?: boolean }>`
  width: 100%;
  height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  max-height: ${props => props.thumbnailMode ? 'none' : '300px'};
  max-width: 350px; // Increased to match other media components
  display: block;
  background: #000;
  object-fit: ${props => props.thumbnailMode ? 'cover' : 'contain'};
  border-radius: 8px; // Added for better appearance
`;

// Add this component for better play button overlay
const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  
  .play-indicator {
    width: 50px;
    height: 50px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; 
  align-items: center;
  justify-content: center;
  color: white;
`;

const DocumentPreview = styled.div`
  padding: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 350px; // Set explicit max width
  
  .doc-icon {
    color: #e94235;
    min-width: 40px;
    display: flex;
    justify-content: center;
  }
  
  .doc-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    
    .filename {
      font-weight: 500;
      color: #1f1f1f;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .filetype {
      font-size: 12px;
      color: #64748b;
    }
  }
`;

const DownloadButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const PreviewModal = styled(Modal)`
  .ant-modal-content {
    background: transparent;
    box-shadow: none;
    padding: 0;
  }
  
  .ant-modal-close {
    top: 16px;
    right: 16px;
    color: white;
    background: rgba(0,0,0,0.5);
    border-radius: 50%;
    padding: 8px;
    z-index: 1000;

    &:hover {
      background: rgba(0,0,0,0.7);
    }
  }

  .preview-content {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    
    &.document {
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-height: 90vh;
      overflow-y: auto;
    }
  }
`;

const ProgressIndicator = styled.div<{ progress: number, thumbnailMode?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: ${props => props.thumbnailMode ? '2px' : '3px'};
  background: #3b82f6;
  width: ${props => props.progress}%;
  transition: width 0.2s;
  z-index: 5;
`;

// Types
interface MediaMessage {
  _id: string;
  messageType: 'video' | 'image' | 'document' | 'audio'; // Added audio
  downloadUrl?: string;
  message: {
    body?: string;
    id?: string;
    mime_type?: string;
    filename?: string;
    voice?: boolean; // Added voice property
  };
}

interface MessageMediaProps {
  message: MediaMessage;
  onDownload: () => Promise<string | null>; // Changed to return URL
  onPreview?: () => void;
  integrationInfo: any;
  thumbnailMode?: boolean;
  isInGroup?: boolean;
}

export const MessageMedia: React.FC<MessageMediaProps> = ({
  message,
  onDownload,
  onPreview,
  integrationInfo,
  thumbnailMode = false,
  isInGroup = false
}) => {
  logger.log("MessageMedia - Rendering media:", message._id, message.messageType, { thumbnailMode, isInGroup });
  const dispatch = useAppDispatch();
  
  const [barHeights] = useState(() => 
    Array.from({ length: 30 }, () => 10 + Math.random() * 25)
  );

  // Add these state variables inside the MessageMedia component
const [audioProgress, setAudioProgress] = useState(0);
const [audioDuration, setAudioDuration] = useState(0);
const [audioCurrentTime, setAudioCurrentTime] = useState(0);
const [isPlaying, setIsPlaying] = useState(false);
const [speedIndicator, setSpeedIndicator] = useState('');
const audioRef = useRef<HTMLAudioElement>(null);

  // Use Redux selectors for loading and error states
  const isLoading = useAppSelector(selectIsMediaDownloading(message._id));
  const loadError = useAppSelector(selectMediaError(message._id));
  
  // Keep local state for UI components
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // Cache lookup helper - can be replaced with Redux selector later
  const getCachedUrl = useCallback(() => {
    return message.downloadUrl;
  }, [message.downloadUrl]);


  const handleMediaClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (message.downloadUrl) {
      handlePreview();
      return;
    }
    
    try {
      setDownloadProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Use provided onDownload handler
      await onDownload();
      
      clearInterval(progressInterval);
      setDownloadProgress(100);

      // After successful download, show preview
      handlePreview();
    } catch (error) {
      Logger.log('Media download failed:', error);
    }
  };

  // Add these utility functions inside the MessageMedia component
const handleTimeUpdate = useCallback(() => {
  if (audioRef.current) {
    const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setAudioProgress(progress);
    setAudioCurrentTime(audioRef.current.currentTime);
  }
}, []);

const handleDurationChange = useCallback(() => {
  if (audioRef.current) {
    setAudioDuration(audioRef.current.duration);
  }
}, []);

const formatTime = useCallback((seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}, []);

const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
  if (audioRef.current) {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const position = (e.clientX - rect.left) / progressBar.clientWidth;
    audioRef.current.currentTime = position * audioRef.current.duration;
  }
}, []);

  const handlePreview = () => {
    // Use Redux preview handler if provided
    if (onPreview) {
      Logger.log("MessageMedia - Calling onPreview handler for:", message._id);
      onPreview();
    } else if (message.downloadUrl || getCachedUrl()) {
      // Otherwise use local modal state
      Logger.log("MessageMedia - Opening local preview with URL available");
      setIsPreviewOpen(true);
    } else {
      Logger.log("MessageMedia - Error: Tried to preview media without URL");
      // Handle the error - maybe show an error message or retry download
      toast.error("Media not available for preview");
    }
  };

  const renderMediaContent = () => {
    if (message.downloadUrl || getCachedUrl()) {
      const url = getCachedUrl() || message.downloadUrl;
      logger.log("MessageMedia - Rendering with URL:", url ? "Available" : "Not available");
      
      switch (message.messageType) {
        case 'image':
          return (
            <div className="media-preview">
              <ImagePreview
                src={url}
                alt={message.message.filename || 'Image'}
                loading="lazy"
                onClick={handlePreview}
                thumbnailMode={thumbnailMode}
              />
              {thumbnailMode && (
                <div className="preview-overlay" onClick={handlePreview}>
                  <div className="play-indicator"></div>
                </div>
              )}
            </div>
          );
          
          case 'video':
            return (
              <div className="media-preview" style={{ position: 'relative' }}>
                <VideoPreview 
                  controls={!thumbnailMode} 
                  preload="metadata"
                  thumbnailMode={thumbnailMode}
                >
                  <source src={url} type={message.message.mime_type} />
                </VideoPreview>
                
                {!isPlaying && (
                  <VideoOverlay onClick={handlePreview}>
                    <div className="play-indicator">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    </div>
                  </VideoOverlay>
                )}
              </div>
            );

        case 'audio':
  
  return (
    <AudioWrapper>
      <div className="audio-waveform" style={{ width: '70%' }}>
    <EnhancedAudioPlayer>
      <audio 
        ref={audioRef}
        src={url} 
        onTimeUpdate={handleTimeUpdate}
        onDurationChange={handleDurationChange}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        hidden
      />
      
      <WaveformVisualization>
        {barHeights.map((height, index) => {
          const isPlayed = audioProgress > (index / barHeights.length) * 100;
          
          return (
            <WaveformBar
              key={index}
              style={{ height: `${height}px` }}
              isPlayed={isPlayed}
            />
          );
        })}
      </WaveformVisualization>
      
      <AudioProgress onClick={handleProgressClick}>
        <ProgressBar progress={audioProgress} />
      </AudioProgress>
      
      <TimeDisplay>
        <span>{formatTime(audioCurrentTime)}</span>
        <span>{formatTime(audioDuration)}</span>
      </TimeDisplay>
      
      <AudioControls>
        <PlayButton
          onClick={() => {
            if (audioRef.current) {
              if (isPlaying) {
                audioRef.current.pause();
              } else {
                audioRef.current.play();
              }
            }
          }}
        >
          {isPlaying ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )}
        </PlayButton>
        
       
        
        <ControlButton
          onClick={() => {
            if (url) {
              // Create a temporary anchor to download the file
              const a = document.createElement('a');
              a.href = url;
              a.download = message.message.filename || 'audio';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </ControlButton>
      </AudioControls>
    </EnhancedAudioPlayer>
    </div>
    </AudioWrapper>
  );
        
        case 'document':
          return (
            <DocumentPreview onClick={handlePreview}>
              <div className="doc-icon">
                <FileText size={24} />
              </div>
              <div className="doc-info">
                <span className="filename">
                  {message.message.filename || 'Document'}
                </span>
                <span className="filetype">
                  {message.message.mime_type?.split('/')[1]?.toUpperCase() || 'FILE'}
                </span>
              </div>
            </DocumentPreview>
          );
      }
    }
  
    // Better placeholder with type-specific icons
    return (
      <MediaPlaceholder onClick={handleMediaClick} thumbnailMode={thumbnailMode}>
        <div className="icon-wrapper">
          {message.messageType === 'image' && <ImageIcon size={thumbnailMode ? 18 : 24} color="#3b82f6" />}
          {message.messageType === 'video' && <Video size={thumbnailMode ? 18 : 24} color="#3b82f6" />}
          {message.messageType === 'document' && <FileText size={thumbnailMode ? 18 : 24} color="#3b82f6" />}
          {message.messageType === 'audio' && (
            <svg width={thumbnailMode ? "18" : "24"} height={thumbnailMode ? "18" : "24"} viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" x2="12" y1="19" y2="22"/>
            </svg>
          )}
        </div>
        {!thumbnailMode && (
          <div className="text">
            Click to download {message.messageType}
          </div>
        )}
      </MediaPlaceholder>
    );
  };

  const renderPreviewContent = () => {
    const url = getCachedUrl() || message.downloadUrl;
    if (!url) return null;
  
    switch (message.messageType) {
      case 'image':
        return (
          <div className="preview-content">
            <img 
              src={url}
              alt={message.message.filename || 'Image'}
              style={{
                maxHeight: '90vh',
                maxWidth: '90vw',
                objectFit: 'contain'
              }}
            />
          </div>
        );
  
      case 'video':
        return (
          <div className="preview-content">
            <video
              src={url}
              controls
              autoPlay
              style={{
                maxHeight: '90vh',
                maxWidth: '90vw'
              }}
            />
          </div>
        );
      
      case 'audio':
        return (
          <div className="preview-content">
            <audio 
              controls 
              autoPlay
              style={{ width: '100%', maxWidth: '500px' }}
            >
              <source src={url} type={message.message.mime_type} />
              Your browser does not support the audio element.
            </audio>
          </div>
        );
  
      case 'document':
        if (message.message.mime_type?.includes('pdf')) {
          return (
            <div className="preview-content document">
              <iframe
                src={url}
                style={{
                  width: '100%',
                  height: '80vh',
                  border: 'none'
                }}
                title="Document Preview"
              />
            </div>
          );
        }
        return (
          <div className="preview-content document">
            <div className="download-prompt">
              <FileText size={48} className="mb-4" />
              <h3>{message.message.filename}</h3>
              <a 
                href={url}
                download
                className="download-button"
                onClick={(e) => e.stopPropagation()}
              >
                Download File
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <MediaWrapper thumbnailMode={thumbnailMode}>
      <MediaContent>
        {renderMediaContent()}
        
        {isLoading && (
          <LoadingOverlay>
            <Spin size={thumbnailMode ? "small" : "default"} />
          </LoadingOverlay>
        )}
  
        {loadError && (
          <LoadingOverlay>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ff4444', marginBottom: thumbnailMode ? '4px' : '8px', fontSize: thumbnailMode ? '11px' : '14px' }}>
                Error
              </div>
              {!thumbnailMode && (
                <button 
                  onClick={handleMediaClick}
                  style={{
                    padding: '6px 12px',
                    background: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          </LoadingOverlay>
        )}
  
        {downloadProgress > 0 && downloadProgress < 100 && (
          <ProgressIndicator progress={downloadProgress} thumbnailMode={thumbnailMode} />
        )}
      </MediaContent>
  
      {/* Only render the local modal if not using Redux preview */}
      {!onPreview && (
        <PreviewModal
          open={isPreviewOpen}
          onCancel={() => setIsPreviewOpen(false)}
          footer={null}
          width="auto"
          centered
          closeIcon={<X className="text-white" />}
        >
          {renderPreviewContent()}
        </PreviewModal>
      )}
    </MediaWrapper>
  );
};

export default MessageMedia;